import './adminDashboard.scss';

import { useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import CuponsEvento from '../../components/admin/CuponsEvento';
import { ConfirmacaoRing, ResumoDashboard } from './adminDashboardResumo';
import { useAdminDashboardViewModel } from './adminDashboardViewModel';

/* ── Helpers ─────────────────────────────────────────────────────── */
function fmtHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}
function fmtDia(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  });
}
function initials(nome) {
  return (nome ?? '?')
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

/* ── Sub-componentes ─────────────────────────────────────────────── */
function Avatar({ nome, size = 32 }) {
  const colors = [
    '#ff2d6c',
    '#7c5cbf',
    '#0369a1',
    '#059669',
    '#d97706',
    '#be185d',
  ];
  const idx = (nome?.charCodeAt(0) ?? 0) % colors.length;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: colors[idx] + '22',
        border: `1.5px solid ${colors[idx]}44`,
        color: colors[idx],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.33,
        fontWeight: 900,
        flexShrink: 0,
      }}
    >
      {initials(nome)}
    </div>
  );
}

/* Card de métrica "hero": destaque visual da Receita Confirmada.
   Ocupa 2 das 4 colunas no desktop; largura total em telas estreitas. */
function HeroMetricCard({ label, value, sub }) {
  return (
    <div className="sm:col-span-2 bg-wine-deep text-white rounded-2xl p-5 sm:p-6 flex flex-col justify-between min-h-[128px]">
      <div className="text-[11px] font-black uppercase tracking-wider text-white/70">
        {label}
      </div>
      <div className="font-gilroy font-extrabold text-[32px] sm:text-[40px] leading-none mt-2 tabular-nums">
        {value}
      </div>
      {sub && <div className="text-xs text-white/70 mt-2">{sub}</div>}
    </div>
  );
}

/* Cards de métrica secundários, cada um com um hue diferente do
   sistema editorial de 6 cores (ver src/design/tokens.css). As
   classes são passadas como strings completas (não construídas em
   runtime) para que o Tailwind as reconheça no scan estático. */
function HueMetricCard({
  label,
  value,
  sub,
  bgClass,
  labelClass,
  valueClass,
  visual,
}) {
  return (
    <div
      className={`rounded-2xl p-5 flex flex-col justify-between min-h-[128px] ${bgClass}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className={`text-[11px] font-black uppercase tracking-wider ${labelClass}`}
        >
          {label}
        </div>
        {visual && <div className={valueClass}>{visual}</div>}
      </div>
      <div
        className={`font-gilroy font-extrabold text-[26px] leading-none mt-2 tabular-nums ${valueClass}`}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-ink-soft/70 mt-2">{sub}</div>}
    </div>
  );
}

/* Breakdown por tipo de inscrição: barra horizontal empilhada +
   legenda com bolinhas. Só o Avulso usa o hue da marca: coral e damasco
   são vizinhos demais do rosa e os segmentos se fundiam numa faixa só,
   por isso as outras duas séries saem de fora da família quente. */
function TicketBreakdown({ resumo, formatarReais }) {
  const grupos = [
    {
      key: 'avulso',
      label: 'Avulso',
      dados: resumo.avulso,
      barClass: 'bg-h-rosa-fill',
      dotClass: 'bg-h-rosa-fill',
    },
    {
      key: 'mensalista',
      label: 'Lily Member',
      dados: resumo.mensalista,
      barClass: 'bg-[#2a78d6]',
      dotClass: 'bg-[#2a78d6]',
    },
    {
      key: 'social',
      label: 'Ingresso Social',
      dados: resumo.social,
      barClass: 'bg-[#c98500]',
      dotClass: 'bg-[#c98500]',
    },
  ].filter((g) => g.dados);

  const totalGeral = grupos.reduce((acc, g) => acc + (g.dados.total ?? 0), 0);
  if (totalGeral === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-parchment p-5 sm:p-6 mb-5">
      <div className="text-[11px] font-black uppercase tracking-wider text-graphite mb-4">
        Inscrições por tipo
      </div>

      <div className="h-3.5 w-full rounded-full overflow-hidden flex gap-0.5 bg-white">
        {grupos.map((g) => {
          const pct = totalGeral > 0 ? (g.dados.total / totalGeral) * 100 : 0;
          if (pct <= 0) return null;
          return (
            <div
              key={g.key}
              className={`${g.barClass} min-w-1.5`}
              style={{ width: `${pct}%` }}
              title={`${g.label}: ${g.dados.total}`}
            />
          );
        })}
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
        {grupos.map((g) => (
          <div key={g.key} className="flex items-center gap-2 text-xs">
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${g.dotClass}`}
            />
            <span className="font-bold text-ink">{g.label}</span>
            <span className="text-graphite">
              {g.dados.confirmadas}
              {g.dados.pendentes > 0 && (
                <span className="font-bold text-ink">
                  {' '}
                  +{g.dados.pendentes} pendente
                  {g.dados.pendentes > 1 ? 's' : ''}
                </span>
              )}
              {' · '}
              {formatarReais(g.dados.receitaCentavos)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HorarioOcupacao({ inscricoes }) {
  // Agrupa inscrições por horário
  const mapa = {};
  for (const ins of inscricoes) {
    if (!ins.horario) continue;
    const k = ins.horario.id;
    if (!mapa[k])
      mapa[k] = { ...ins.horario, confirmadas: 0, pendentes: 0, total: 0 };
    mapa[k].total++;
    if (ins.status === 'CONFIRMED') mapa[k].confirmadas++;
    if (ins.status === 'PENDING') mapa[k].pendentes++;
  }
  const slots = Object.values(mapa).sort(
    (a, b) => new Date(a.startDate) - new Date(b.startDate),
  );
  if (slots.length === 0) return null;

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        border: '1.5px solid #f1f5f9',
        padding: '20px 24px',
        marginBottom: 20,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 900,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          marginBottom: 16,
        }}
      >
        📅 Ocupação por horário
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {slots.map((s) => {
          const cap = s.slots ?? null;
          const pct = cap ? Math.round((s.occupiedSlots / cap) * 100) : null;
          const barCor =
            pct > 85 ? '#dc2626' : pct > 60 ? '#d97706' : '#22c55e';
          return (
            <div
              key={s.id}
              style={{
                background: '#f8fafc',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                padding: '12px 16px',
                minWidth: 160,
                flex: 1,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: '#0f172a',
                  marginBottom: 2,
                }}
              >
                {fmtDia(s.startDate)}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                {fmtHora(s.startDate).split(', ')[1] ?? fmtHora(s.startDate)}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: '#64748b',
                  marginBottom: 4,
                }}
              >
                <span style={{ color: '#166534', fontWeight: 700 }}>
                  ✓ {s.confirmadas}
                </span>
                {s.pendentes > 0 && (
                  <span style={{ color: '#d97706', fontWeight: 700 }}>
                    ⏳ {s.pendentes}
                  </span>
                )}
                {cap && (
                  <span>
                    {s.occupiedSlots}/{cap} vagas
                  </span>
                )}
              </div>
              {cap && (
                <div
                  style={{ height: 8, background: '#e2e8f0', borderRadius: 99 }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(pct, 100)}%`,
                      background: barCor,
                      borderRadius: 99,
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PendentesAlert({ pendentes, formatarReais }) {
  if (pendentes.length === 0) return null;

  function ticketLabel(type) {
    if (type === 'MENSALISTA') return 'Lily Member';
    if (type === 'SOCIAL') return 'Social';
    return 'Avulso';
  }

  return (
    <div
      style={{
        background: '#fffbeb',
        borderRadius: 16,
        border: '1.5px solid #fde68a',
        padding: '20px 24px',
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 900, color: '#92400e' }}>
            ⏳ {pendentes.length} pagamento{pendentes.length > 1 ? 's' : ''}{' '}
            pendente{pendentes.length > 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: 11, color: '#a16207', marginTop: 2 }}>
            Potencial:{' '}
            {formatarReais(pendentes.reduce((a, i) => a + i.amountInCents, 0))}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {pendentes.map((ins) => (
          <div
            key={ins.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: '#fff',
              borderRadius: 10,
              padding: '10px 14px',
              border: '1px solid #fde68a',
            }}
          >
            <Avatar nome={ins.nome} size={30} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#0f172a' }}>
                {ins.nome}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>{ins.email}</div>
            </div>
            <div style={{ fontSize: 10, color: '#64748b', textAlign: 'right' }}>
              <div>{ticketLabel(ins.ticketType)}</div>
              {ins.horario?.startDate && (
                <div style={{ color: '#94a3b8' }}>
                  {fmtHora(ins.horario.startDate)}
                </div>
              )}
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#92400e',
                minWidth: 64,
                textAlign: 'right',
              }}
            >
              {formatarReais(ins.amountInCents)}
            </div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 900,
                padding: '2px 8px',
                borderRadius: 99,
                background:
                  ins.pagamento?.billingType === 'PIX' ? '#fef9c3' : '#ede9fe',
                color:
                  ins.pagamento?.billingType === 'PIX' ? '#854d0e' : '#5b21b6',
                border: `1px solid ${ins.pagamento?.billingType === 'PIX' ? '#fde68a' : '#c4b5fd'}`,
              }}
            >
              {ins.pagamento?.billingType === 'PIX'
                ? 'PIX'
                : ins.pagamento?.billingType
                  ? 'Cartão'
                  : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NovaInscricaoToast({ inscricao, onDismiss }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#fff',
        border: '1.5px solid #ff2d6c33',
        borderRadius: 16,
        padding: '12px 16px',
        boxShadow: '0 8px 24px rgba(255,45,108,0.12)',
        minWidth: 280,
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: '#22c55e',
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: '#0f172a' }}>
          Nova inscrição! 🎉
        </div>
        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
          {inscricao.nome} acabou de se inscrever
        </div>
      </div>
      <button
        onClick={() => onDismiss(inscricao.id)}
        style={{
          background: 'none',
          border: 'none',
          color: '#94a3b8',
          cursor: 'pointer',
          fontSize: 14,
        }}
      >
        ×
      </button>
    </div>
  );
}

/* Badge do tipo de ingresso (coluna "Tipo" da tabela). Classes
   Tailwind completas (literais) usando os hues da marca — mesmo
   mapeamento de cor da barra empilhada acima, para consistência. */
function ticketLabel(type) {
  if (type === 'MENSALISTA')
    return { label: 'Lily Member', cls: 'bg-h-coral-wash text-h-coral-text' };
  if (type === 'SOCIAL')
    return {
      label: 'Social',
      cls: 'bg-h-damasco-wash text-h-damasco-text',
    };
  return { label: 'Avulso', cls: 'bg-h-rosa-wash text-h-rosa-text' };
}

/* Badge de status da inscrição (coluna "Status" da tabela).
   Confirmado mantém verde semântico (sucesso é universal); Pendente e
   Cancelado usam os tokens da marca em vez de amarelo/vermelho
   genéricos. */
function StatusPill({ status }) {
  const map = {
    CONFIRMED: { label: 'Confirmado', cls: 'bg-green-100 text-green-700' },
    PENDING: {
      label: 'Pendente',
      cls: 'bg-h-damasco-wash text-h-damasco-text',
    },
    CANCELLED: { label: 'Cancelado', cls: 'bg-parchment text-graphite' },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${s.cls}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
      {s.label}
    </span>
  );
}

/* Navegação por abas: a Visão Geral acumulava resumo global + métricas +
   breakdown + ocupação + pendentes + tabela inteira na mesma rolagem.
   Dividido em 3 focos: panorama (geral), operação do evento selecionado
   (evento) e a lista de trabalho (inscrições). */
const ABAS = [
  { id: 'geral', label: 'Visão Geral' },
  { id: 'evento', label: 'Evento' },
  { id: 'inscricoes', label: 'Inscrições' },
  { id: 'cupons', label: 'Cupons' },
];

function TabNav({ aba, setAba }) {
  return (
    <nav
      aria-label="Seções do dashboard"
      className="flex gap-1 mb-5 border-b border-parchment overflow-x-auto"
    >
      {ABAS.map((a) => (
        <button
          key={a.id}
          onClick={() => setAba(a.id)}
          className={`px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${
            aba === a.id
              ? 'border-wine text-wine'
              : 'border-transparent text-graphite hover:text-ink'
          }`}
        >
          {a.label}
        </button>
      ))}
    </nav>
  );
}

/* ── View principal ──────────────────────────────────────────────── */
export default function AdminDashboardView() {
  const vm = useAdminDashboardViewModel();
  const navigate = useNavigate();

  const resumo = vm.dashboard?.resumo ?? {};
  const inscricoes = vm.dashboard?.inscricoes ?? [];
  const eventoAtual = vm.eventos.find((e) => e.id === vm.eventoSelecionado);
  const pendentes = inscricoes.filter((i) => i.status === 'PENDING');
  const txConf =
    resumo.total > 0
      ? Math.round((resumo.confirmadas / resumo.total) * 100)
      : 0;

  return (
    <AdminLayout>
      <div className="dashboard-container">
        <div className="main-content">
          {/* ── HEADER ─────────────────────────────────────────────── */}
          <header
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: 24,
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <h1
                className="font-gilroy"
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  color: '#0f172a',
                  margin: 0,
                }}
              >
                {eventoAtual ? eventoAtual.title : 'Dashboard'}
              </h1>
              <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0' }}>
                Resumo de vendas e inscrições
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={vm.alternarValoresOcultos}
                title={
                  vm.valoresOcultos
                    ? 'Mostrar valores financeiros'
                    : 'Ocultar valores financeiros'
                }
                aria-label={
                  vm.valoresOcultos
                    ? 'Mostrar valores financeiros'
                    : 'Ocultar valores financeiros'
                }
                style={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: 10,
                  background: vm.valoresOcultos ? '#fff5f7' : '#fff',
                  color: vm.valoresOcultos ? '#e8336d' : '#64748b',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {vm.valoresOcultos ? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.52 13.52 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                    <line x1="2" y1="2" x2="22" y2="22" />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
              <select
                value={vm.eventoSelecionado}
                onChange={(e) => vm.setEventoSelecionado(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: 10,
                  fontSize: 13,
                  color: '#0f172a',
                  background: '#fff',
                  cursor: 'pointer',
                  maxWidth: 220,
                }}
              >
                <option value="">Últimos 60 dias</option>
                {vm.eventos.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title}
                  </option>
                ))}
              </select>
              <button
                onClick={vm.recarregar}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#e8336d',
                  background: '#fff5f7',
                  border: '1.5px solid #fecdd3',
                  borderRadius: 10,
                  padding: '8px 14px',
                  cursor: 'pointer',
                }}
              >
                ↻ Atualizar
              </button>
              <button
                onClick={() => navigate('/admin/inscricoes')}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#0f172a',
                  background: '#f8fafc',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '8px 14px',
                  cursor: 'pointer',
                }}
              >
                Ver horários →
              </button>
            </div>
          </header>

          {/* ── TOASTS ──────────────────────────────────────────────── */}
          {vm.novasInscricoes.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                marginBottom: 16,
              }}
            >
              {vm.novasInscricoes.map((ins) => (
                <NovaInscricaoToast
                  key={ins.id}
                  inscricao={ins}
                  onDismiss={vm.descartarNotificacao}
                />
              ))}
            </div>
          )}

          {vm.erroApi && (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 12,
                padding: '10px 16px',
                fontSize: 12,
                color: '#dc2626',
                marginBottom: 16,
              }}
            >
              {vm.erroApi}
            </div>
          )}

          <TabNav aba={vm.abaSelecionada} setAba={vm.setAbaSelecionada} />

          {/* ── ABA: VISÃO GERAL ─────────────────────────────────────── */}
          {vm.abaSelecionada === 'geral' && (
            <>
              <ResumoDashboard
                resumo={vm.resumo}
                carregando={vm.carregandoResumo}
                erro={vm.erroResumo}
                formatarReais={vm.formatarReais}
                HueMetricCard={HueMetricCard}
              />

              {vm.carregando ? (
                <div
                  style={{ textAlign: 'center', color: '#94a3b8', padding: 64 }}
                >
                  Carregando…
                </div>
              ) : (
                /* Grid nativo do Tailwind (não ResponsiveGrid): o card de
                   Receita precisa ocupar 2 colunas no desktop, o que exige
                   CSS grid de verdade. grid-flow-dense evita buracos no
                   tablet quando o card de 2 colunas não cabe na fileira. */
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 grid-flow-dense gap-3 mb-5">
                  <HueMetricCard
                    label="Total de inscritas"
                    value={resumo.total ?? 0}
                    sub={`${resumo.confirmadas ?? 0} confirmadas · ${resumo.pendentes ?? 0} pendentes`}
                    bgClass="bg-h-rosa-wash"
                    labelClass="text-h-rosa-text"
                    valueClass="text-h-rosa-display"
                  />
                  <HeroMetricCard
                    label="Receita confirmada"
                    value={vm.formatarReais(resumo.receitaTotalCentavos ?? 0)}
                    sub="Apenas inscrições confirmadas"
                  />
                  <HueMetricCard
                    label="Taxa de confirmação"
                    value={`${txConf}%`}
                    sub={`${resumo.confirmadas ?? 0} de ${resumo.total ?? 0} confirmadas`}
                    bgClass="bg-h-damasco-wash"
                    labelClass="text-h-damasco-text"
                    valueClass="text-h-damasco-display"
                    visual={<ConfirmacaoRing percentual={txConf / 100} />}
                  />
                  {pendentes.length > 0 && (
                    <HueMetricCard
                      label="A receber (pendentes)"
                      value={vm.formatarReais(
                        pendentes.reduce((a, i) => a + i.amountInCents, 0),
                      )}
                      sub={`${pendentes.length} pagamento${pendentes.length > 1 ? 's' : ''} aguardando`}
                      bgClass="bg-h-vinho-wash"
                      labelClass="text-h-vinho-text"
                      valueClass="text-h-vinho-display"
                    />
                  )}
                </div>
              )}
            </>
          )}

          {/* ── ABA: EVENTO (breakdown, ocupação, pendentes) ─────────── */}
          {vm.abaSelecionada === 'evento' &&
            (vm.carregando ? (
              <div
                style={{ textAlign: 'center', color: '#94a3b8', padding: 64 }}
              >
                Carregando…
              </div>
            ) : (
              <>
                <TicketBreakdown
                  resumo={resumo}
                  formatarReais={vm.formatarReais}
                />
                <HorarioOcupacao inscricoes={inscricoes} />
                <PendentesAlert
                  pendentes={pendentes}
                  formatarReais={vm.formatarReais}
                />
              </>
            ))}

          {/* ── ABA: CUPONS (do evento selecionado) ──────────────────── */}
          {vm.abaSelecionada === 'cupons' &&
            (eventoAtual ? (
              <CuponsEvento
                key={eventoAtual.id}
                eventId={eventoAtual.id}
                precos={[
                  {
                    label: 'Ingresso Avulso',
                    centavos: eventoAtual.amountInCents,
                  },
                  {
                    label: 'Lily Member',
                    centavos: eventoAtual.memberAmountInCents,
                    soMembro: true,
                  },
                ]}
              />
            ) : (
              <div className="bg-white rounded-2xl border border-parchment p-8 text-center text-sm text-graphite">
                Escolha um evento no seletor acima para ver e criar cupons.
              </div>
            ))}

          {/* ── ABA: INSCRIÇÕES (tabela completa) ────────────────────── */}
          {vm.abaSelecionada === 'inscricoes' &&
            (vm.carregando ? (
              <div
                style={{ textAlign: 'center', color: '#94a3b8', padding: 64 }}
              >
                Carregando…
              </div>
            ) : (
              <div
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  border: '1.5px solid #f1f5f9',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 900,
                          fontSize: 15,
                          color: '#0f172a',
                        }}
                      >
                        Todas as inscrições
                      </div>
                      <div
                        style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}
                      >
                        {resumo.confirmadas ?? 0} confirmadas ·{' '}
                        {resumo.pendentes ?? 0} aguardando pagamento
                      </div>
                    </div>
                    <button
                      onClick={vm.exportarCsvFiltrado}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#0f172a',
                        background: '#f8fafc',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: 10,
                        padding: '8px 14px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ↓ Exportar CSV
                    </button>
                  </div>

                  {/* ── BUSCA + FILTROS ──────────────────────────────── */}
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      value={vm.busca}
                      onChange={(e) => vm.setBusca(e.target.value)}
                      placeholder="Buscar por nome, e-mail, CPF ou WhatsApp…"
                      style={{
                        flex: '1 1 220px',
                        fontSize: 12,
                        padding: '8px 12px',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: 10,
                        color: '#0f172a',
                        outline: 'none',
                      }}
                    />
                    <select
                      value={vm.filtroTipo}
                      onChange={(e) => vm.setFiltroTipo(e.target.value)}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '8px 10px',
                        borderRadius: 10,
                        border: '1.5px solid #e2e8f0',
                        color: '#64748b',
                        background: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {['Todos', 'Avulso', 'Lily Member', 'Social'].map((t) => (
                        <option key={t} value={t}>
                          {t === 'Todos' ? 'Todos os tipos' : t}
                        </option>
                      ))}
                    </select>
                    <select
                      value={vm.filtroPagamento}
                      onChange={(e) => vm.setFiltroPagamento(e.target.value)}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '8px 10px',
                        borderRadius: 10,
                        border: '1.5px solid #e2e8f0',
                        color: '#64748b',
                        background: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="Todos">Todos os pagamentos</option>
                      <option value="PIX">PIX</option>
                      <option value="CREDIT_CARD">Cartão</option>
                      <option value="Sem pagamento">Sem pagamento</option>
                    </select>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['Todos', 'Confirmado', 'Pendente', 'Cancelado'].map(
                        (f) => (
                          <button
                            key={f}
                            onClick={() => vm.setFiltroStatus(f)}
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: '6px 14px',
                              borderRadius: 99,
                              border: '1.5px solid',
                              cursor: 'pointer',
                              background:
                                vm.filtroStatus === f ? '#ff2d6c' : '#fff',
                              color: vm.filtroStatus === f ? '#fff' : '#64748b',
                              borderColor:
                                vm.filtroStatus === f ? '#ff2d6c' : '#e2e8f0',
                            }}
                          >
                            {f}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                </div>

                {/* overflowX:auto garante rolagem horizontal da tabela em
                    telas estreitas, em vez de espremer as colunas. */}
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      minWidth: 640,
                      borderCollapse: 'collapse',
                    }}
                  >
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        {[
                          { label: 'Participante', campo: 'nome' },
                          { label: 'Contato' },
                          { label: 'Horário', campo: 'horario' },
                          { label: 'Tipo' },
                          { label: 'Pgto' },
                          { label: 'Valor', campo: 'valor' },
                          { label: 'Status' },
                        ].map(({ label, campo }) => {
                          const ativo = campo && vm.ordenacao.campo === campo;
                          return (
                            <th
                              key={label}
                              onClick={
                                campo
                                  ? () => vm.alternarOrdenacao(campo)
                                  : undefined
                              }
                              style={{
                                padding: '10px 16px',
                                textAlign: 'left',
                                fontSize: 10,
                                fontWeight: 900,
                                color: ativo ? '#e8336d' : '#94a3b8',
                                textTransform: 'uppercase',
                                letterSpacing: '.06em',
                                whiteSpace: 'nowrap',
                                cursor: campo ? 'pointer' : 'default',
                                userSelect: 'none',
                              }}
                            >
                              {label}
                              {ativo &&
                                (vm.ordenacao.direcao === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {vm.inscricoesPaginadas.map((ins) => {
                        const tkt = ticketLabel(ins.ticketType);
                        const horario = ins.horario?.startDate
                          ? fmtHora(ins.horario.startDate)
                          : '—';
                        const isPending = ins.status === 'PENDING';
                        return (
                          <tr
                            key={ins.id}
                            style={{
                              borderTop: '1px solid #f8fafc',
                              background: isPending ? '#fffdf0' : 'transparent',
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = isPending
                                ? '#fef9c3'
                                : '#fafafa')
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = isPending
                                ? '#fffdf0'
                                : 'transparent')
                            }
                          >
                            <td style={{ padding: '12px 16px' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                }}
                              >
                                <Avatar nome={ins.nome} />
                                <div>
                                  <div
                                    style={{
                                      fontWeight: 700,
                                      fontSize: 13,
                                      color: '#0f172a',
                                    }}
                                  >
                                    {ins.nome}
                                  </div>
                                  <div
                                    style={{ fontSize: 10, color: '#94a3b8' }}
                                  >
                                    {vm.formatarData(ins.createdAt)}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ fontSize: 12, color: '#0f172a' }}>
                                {ins.email}
                              </div>
                              <div style={{ fontSize: 10, color: '#94a3b8' }}>
                                {ins.whatsapp}
                              </div>
                            </td>
                            <td
                              style={{
                                padding: '12px 16px',
                                fontSize: 12,
                                color: '#64748b',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {horario}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span
                                className={`inline-block text-[9px] font-black tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${tkt.cls}`}
                              >
                                {tkt.label}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {ins.pagamento ? (
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 900,
                                    padding: '2px 8px',
                                    borderRadius: 99,
                                    background:
                                      ins.pagamento.billingType === 'PIX'
                                        ? '#fef9c3'
                                        : '#ede9fe',
                                    color:
                                      ins.pagamento.billingType === 'PIX'
                                        ? '#854d0e'
                                        : '#5b21b6',
                                    border: `1px solid ${ins.pagamento.billingType === 'PIX' ? '#fcd34d' : '#c4b5fd'}`,
                                  }}
                                >
                                  {ins.pagamento.billingType === 'PIX'
                                    ? 'PIX'
                                    : 'Cartão'}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td
                              style={{
                                padding: '12px 16px',
                                fontWeight: 700,
                                fontSize: 13,
                                color: '#0f172a',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {vm.formatarReais(ins.amountInCents)}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <StatusPill status={ins.status} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {vm.inscricoesFiltradas.length === 0 && (
                    <div
                      style={{
                        padding: '48px',
                        textAlign: 'center',
                        color: '#94a3b8',
                        fontSize: 13,
                      }}
                    >
                      Nenhuma inscrição encontrada para os filtros atuais.
                    </div>
                  )}
                </div>

                <div
                  style={{
                    padding: '12px 24px',
                    background: '#f8fafc',
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>
                    Mostrando {vm.inscricoesPaginadas.length} de{' '}
                    {vm.inscricoesFiltradas.length} inscrições
                    {vm.inscricoesFiltradas.length !== inscricoes.length &&
                      ` (${inscricoes.length} no total)`}
                  </span>
                  {vm.temMaisInscricoes && (
                    <button
                      onClick={vm.carregarMaisInscricoes}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#e8336d',
                        background: '#fff',
                        border: '1.5px solid #fecdd3',
                        borderRadius: 10,
                        padding: '6px 14px',
                        cursor: 'pointer',
                      }}
                    >
                      Carregar mais
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </AdminLayout>
  );
}
